import { memo, useEffect, useRef, type FocusEvent, type KeyboardEvent, type PointerEvent } from 'react';
import clsx from 'clsx';
import { Separator as PanelSeparator } from 'react-resizable-panels';

import styles from './separator.module.scss';

const HANDLE_MIN_Y = 10;
const HANDLE_MAX_Y = 90;
const PANEL_MIN_WIDTH_EPSILON = 1;
// Pointer travel below this is a click on the collapsed edge rather than the start of a drag.
const CLICK_THRESHOLD = 3;

type SeparatorLimit = 'none' | 'left' | 'right';

export const Separator = memo((props: SeparatorProps) => {
  const {
    disabled = false,
    visible = true,
    leftPanelMinWidth,
    rightPanelMinWidth,
    collapsed = false,
    onExpand,
    className,
  } = props;
  const isDisabled = disabled || !visible;

  const pendingSeparatorRef = useRef<HTMLDivElement | null>(null);
  const rafId = useRef<number | null>(null);
  const pointerDownX = useRef<number | null>(null);

  const setSeparatorLimit = (separator: HTMLDivElement, limit: SeparatorLimit) => {
    if (separator.dataset.separatorLimit !== limit) {
      separator.dataset.separatorLimit = limit;
    }
  };

  const updateHandleState = (separator: HTMLDivElement) => {
    const leftPanel = separator.previousElementSibling;
    const rightPanel = separator.nextElementSibling;

    if (!(leftPanel instanceof HTMLElement) || !(rightPanel instanceof HTMLElement)) {
      return setSeparatorLimit(separator, 'none');
    }

    const leftWidth = leftPanel.getBoundingClientRect().width;
    const rightWidth = rightPanel.getBoundingClientRect().width;

    const canMoveLeft = leftWidth > leftPanelMinWidth + PANEL_MIN_WIDTH_EPSILON;
    const canMoveRight = rightWidth > rightPanelMinWidth + PANEL_MIN_WIDTH_EPSILON;

    if (!canMoveLeft && canMoveRight) {
      return setSeparatorLimit(separator, 'left');
    }

    if (canMoveLeft && !canMoveRight) {
      return setSeparatorLimit(separator, 'right');
    }

    setSeparatorLimit(separator, 'none');
  };

  const scheduleHandleStateUpdate = (separator: HTMLDivElement) => {
    pendingSeparatorRef.current = separator;

    if (rafId.current != null) {
      return;
    }

    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;

      const pendingSeparator = pendingSeparatorRef.current;
      pendingSeparatorRef.current = null;

      if (pendingSeparator != null) {
        updateHandleState(pendingSeparator);
      }
    });
  };

  const updateHandlePosition = (event: PointerEvent<HTMLDivElement>) => {
    const separator = event.currentTarget;
    const { top, height } = separator.getBoundingClientRect();

    scheduleHandleStateUpdate(separator);

    if (height === 0) {
      return;
    }

    const relativeY = ((event.clientY - top) / height) * 100;
    const clampedY = Math.min(HANDLE_MAX_Y, Math.max(HANDLE_MIN_Y, relativeY));

    separator.style.setProperty('--separator-handle-y', `${clampedY}%`);
  };

  const handleStateUpdate = (event: KeyboardEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>) => {
    scheduleHandleStateUpdate(event.currentTarget);
  };

  // The library's Enter toggles the panel beside the separator collapsed, but a collapsible sidebar is
  // only collapsed by its own toggle and is held at its minimum instead, so Enter just shrank it. The
  // library skips a handled event, and a capture listener runs before its own on the separator.
  const suppressEnterCollapse = (event: KeyboardEvent<HTMLDivElement>) => {
    event.key === 'Enter' && event.preventDefault();
  };

  const expandOnClick = (event: PointerEvent<HTMLDivElement>) => {
    const downX = pointerDownX.current;
    pointerDownX.current = null;

    if (!collapsed || downX === null || Math.abs(event.clientX - downX) >= CLICK_THRESHOLD) return;

    onExpand?.();
  };

  useEffect(() => {
    return () => {
      rafId.current !== null && cancelAnimationFrame(rafId.current);

      rafId.current = null;
      pendingSeparatorRef.current = null;
    };
  }, []);

  // The collapsed edge stays a way back in even where resizing is off (narrow windows, fixed-width
  // routes): only the drag and the grabber belong to a resizable separator.
  const expandProps = {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      pointerDownX.current = event.clientX;
    },
    onPointerUp: expandOnClick,
  };
  const expandOnly = collapsed && visible;

  const interactionProps = isDisabled
    ? expandOnly
      ? expandProps
      : {}
    : {
        onFocus: handleStateUpdate,
        onKeyDown: handleStateUpdate,
        onKeyDownCapture: suppressEnterCollapse,
        onPointerEnter: updateHandlePosition,
        onPointerMove: updateHandlePosition,
        ...expandProps,
      };

  return (
    <PanelSeparator
      disabled={isDisabled}
      className={clsx(styles.separator, className, {
        [styles.hidden]: !visible,
        [styles.collapsed]: collapsed,
        [styles.expandOnly]: isDisabled && expandOnly,
      })}
      {...interactionProps}
    >
      {!isDisabled && <div className={styles.separatorHandle} aria-hidden />}
    </PanelSeparator>
  );
});

interface SeparatorProps {
  disabled?: boolean;
  visible?: boolean;
  className?: string;
  leftPanelMinWidth: number;
  rightPanelMinWidth: number;
  collapsed?: boolean;
  onExpand?: () => void;
}
